<template>
  <div data-testid="reconciliation" v-if="isTaskBotLinked(taskData)">
    <div class="d-flex align-center justify-center" data-testid="reconciliation__summary-wrapper">
      <v-card class="pa-4" flat min-width="600" data-testid="reconciliation__summary-card">
        <div v-if="loading" class="text-center" data-testid="reconciliation__loading-state">
          <v-progress-circular data-testid="reconciliation__loading-spinner" indeterminate color="primary" />
          <div class="mt-2" data-testid="reconciliation__loading-text">{{ $constants.RECONCILIATION_TABLE.TEXT.LOADING_TEXT }}</div>
        </div>
        <template v-else>
          <div v-if="summaryCollection" data-testid="reconciliation__summary-section">
            <h4 class="mb-3 font-weight-regular" data-testid="reconciliation__summary-title">{{ $constants.RECONCILIATION_TABLE.TEXT.RECONCILIATION_SUMMARY }}</h4>
            <v-simple-table dense class="reconciliation-table border-on" v-if="groupedOverallSummary.length > 0" data-testid="reconciliation__summary-table">
              <template v-slot:default>
                <thead class="tableHeaderBg">
                  <tr class="table-header-row tableHeaderBg" data-testid="reconciliation__summary-header-row">
                    <th class="col-description" data-testid="reconciliation__summary-header-description">{{ $constants.RECONCILIATION_TABLE.TEXT.DESCRIPTION }}</th>
                    <th class="col-sub-amount" data-testid="reconciliation__summary-header-sub-amount">{{ $constants.RECONCILIATION_TABLE.TEXT.SUB_AMOUNT }}</th>
                    <th class="col-final-amount" data-testid="reconciliation__summary-header-final-amount">{{ $constants.RECONCILIATION_TABLE.TEXT.FINAL_AMOUNT }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, k) in groupedOverallSummary" :key="item._key || k" 
                      :class="getRowClass(item)"
                      :data-testid="item._isGroupHeader ? 'reconciliation__summary-group-header' : 'reconciliation__summary-row'">
                    <!-- Group Header Row -->
                    <template v-if="item._isGroupHeader">
                      <td colspan="3" class="group-header-cell" data-testid="reconciliation__summary-group-header-cell">
                        {{ item._groupHeaderText }}
                      </td>
                    </template>
                    <!-- Regular Data Row -->
                    <template v-else>
                      <td :class="{ 'group-item-indent': item._isGroupItem }" data-testid="reconciliation__summary-cell-description">
                        <div v-if="item?.Reconciliation?.includes($constants.RECONCILIATION_TABLE.TEXT.TOTAL_EXCLUDED)">
                          <div v-for="(values, key) in parsedAmount(item.Amount)" :key="key">
                            <div v-if="key === 'ADD' && values.length">
                              <span v-for="(subItem, index) in values" :key="index">
                                <div v-for="(value, key) in subItem" :key="key" data-testid="reconciliation__summary-add-item">
                                  {{ $constants.RECONCILIATION_TABLE.TEXT.ADD_PREFIX }}{{ key }}
                                </div>
                              </span>
                            </div>
                            <div v-if="key === 'LESS' && values.length">
                              <span v-for="(subItem, index) in values" :key="index">
                                <div v-for="(value, key) in subItem" :key="key" data-testid="reconciliation__summary-less-item">
                                  {{ $constants.RECONCILIATION_TABLE.TEXT.LESS_PREFIX }}{{ key }}
                                </div>
                              </span>
                            </div>
                          </div>
                        </div>
                        <div v-else data-testid="reconciliation__summary-description-text">
                          {{ item.Reconciliation }}
                          <span v-if="item.Group === 'Table 1 Total'" class="table-total-badge">Recon Amount</span>
                        </div>
                      </td>

                      <td data-testid="reconciliation__summary-cell-sub-amount">
                        <div v-if="item?.Reconciliation?.includes($constants.RECONCILIATION_TABLE.TEXT.TOTAL_EXCLUDED)">
                          <div v-for="(values, key) in parsedAmount(item.Amount)" :key="key">
                            <div v-if="key === 'ADD' && values.length">
                              <span v-for="(subItem, index) in values" :key="index">
                                <div v-for="(value, key) in subItem" :key="key" class="summary-indent" data-testid="reconciliation__summary-add-value">
                                  {{ formatSubAmount(value) }}
                                </div>
                              </span>
                            </div>
                            <div v-if="key === 'LESS' && values.length">
                              <span v-for="(subItem, index) in values" :key="index">
                                <div v-for="(value, key) in subItem" :key="key" class="summary-indent" data-testid="reconciliation__summary-less-value">
                                  {{ formatSubAmount(value) }}
                                </div>
                              </span>
                            </div>
                          </div>
                        </div>
                        <div v-else-if="item?.Reconciliation?.toLowerCase()?.includes($constants.RECONCILIATION_TABLE.TEXT.UNMATCHED_TOTAL_IN) 
                        || item?.Reconciliation?.toLowerCase()?.includes($constants.RECONCILIATION_TABLE.TEXT.UNMATCHED_TOTAL_IN_TABLE1)" data-testid="reconciliation__summary-unmatched-sub-amount">
                          {{ formatSubAmount(item["Sub Amount"]) }}
                        </div>
                        <div v-else data-testid="reconciliation__summary-regular-sub-amount">
                          {{ formatSubAmount(item["Sub Amount"]) }}
                        </div>
                      </td>

                      <td data-testid="reconciliation__summary-cell-final-amount">
                        <div v-if="item?.Reconciliation?.toLowerCase()?.includes($constants.RECONCILIATION_TABLE.TEXT.TOTAL_AMOUNT_AS_PER) || 
                        item?.Reconciliation?.toLowerCase()?.includes($constants.RECONCILIATION_TABLE.TEXT.TOTAL_AMOUNT_AS_PER_TABLE2) ||
                        item?.Reconciliation?.toLowerCase()?.includes($constants.RECONCILIATION_TABLE.TEXT.GROUP_TABLE_1_TOTAL) ||
                        item?.Reconciliation?.toLowerCase()?.includes($constants.RECONCILIATION_TABLE.TEXT.GROUP_TABLE_2_TOTAL)" data-testid="reconciliation__summary-total-amount">
                          {{ item["Amount"] !== null && item["Amount"] !== undefined ? $nova.formatNumber(item["Amount"].toFixed(0)) : '' }}
                        </div>
                        <div v-else data-testid="reconciliation__summary-nested-final-amount">
                          <div v-if="item.Amount?.ADD" data-testid="reconciliation__summary-add-amount">
                            {{ item.Amount.ADD }}
                          </div>
                        </div>
                      </td>
                    </template>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
            <div v-else class="text-center grey--text title pa-6" data-testid="reconciliation__summary-empty-state">{{ $constants.RECONCILIATION_TABLE.TEXT.NO_DATA_AVAILABLE }}</div>
          </div>
          <ReconciliationEmptyState
            v-else
            data-test-id="reconciliation__analysis-not-run"
          />
        </template>
      </v-card>
    </div>
  </div>
  <div data-testid="reconciliation" v-else-if="hasTableData">
      <div class="d-flex align-center justify-space-between" data-testid="reconciliation__details-header">
          <h4 v-if="!redesign" class="font-weight-regular" data-testid="reconciliation__details-title">{{ $constants.RECONCILIATION_TABLE.TEXT.RECONCILIATION_DETAILS }}</h4>
          <v-select
              data-testid="reconciliation__table-type-select"
              v-if="hasEntityAndGlTables"
              v-model="selectedTableType"
              :items="tableTypes"
              item-text="text"
              item-value="value"
              dense
              outlined
              hide-details
              class="table-selector table-selector-width"
          ></v-select>
      </div>
      
      <!-- Entity Table -->
      <v-simple-table dense class="reconciliation-table border-on radius-small" v-if="showEntityTable" data-testid="reconciliation__entity-table">
          <template v-slot:default>
              <thead class="tableHeaderBg">
                  <tr class="table-header-row tableHeaderBg" data-testid="reconciliation__entity-header-row">
                      <th v-for="(header, idx) in entityTableHeaders" :key="idx" 
                          :class="getHeaderClass(header)" data-testid="reconciliation__entity-header">
                          {{ header }}
                      </th>
                  </tr>
              </thead>
              <transition-group name="row" tag="tbody">
                  <tr v-for="(row, idx) in flattenedEntityTable" :key="row.key || idx" 
                      :class="getTableRowClass(row)" 
                      :data-testid="row.isHeader ? 'reconciliation__entity-group-header' : 'reconciliation__entity-row'"
                      @click="row.isHeader && row.hasChildren ? toggleGroupCollapse(row.groupId) : null">
                      <td v-for="(header, headerIdx) in entityTableHeaders" :key="`${idx}-${headerIdx}`"
                          :class="getTableCellClass(row, header, headerIdx === 0)" 
                          :data-testid="row.isHeader ? 'reconciliation__entity-group-cell' : 'reconciliation__entity-cell'">
                          <template v-if="headerIdx === 0 && row.isHeader && row.hasChildren">
                              <span class="collapse-icon">
                                  <v-icon size="16" :class="row.isCollapsed ? 'collapsed' : 'expanded'">
                                      {{ row.isCollapsed ? 'mdi-chevron-right' : 'mdi-chevron-down' }}
                                  </v-icon>
                              </span>
                          </template>
                          <template v-if="typeof row[header] === 'number'">
                              {{ $nova.formatNumber(row[header].toFixed(0)) }}
                          </template>
                          <template v-else>
                              {{ row[header] }}
                          </template>
                      </td>
                  </tr>
              </transition-group>
          </template>
      </v-simple-table>

      <!-- GL Table -->
      <v-simple-table dense class="reconciliation-table border-on radius-small" v-if="showGlTable" data-testid="reconciliation__gl-table">
          <template v-slot:default>
              <thead class="tableHeaderBg">
                  <tr class="table-header-row tableHeaderBg" data-testid="reconciliation__gl-header-row">
                      <th v-for="(header, idx) in glTableHeaders" :key="idx" 
                          :class="getHeaderClass(header)" data-testid="reconciliation__gl-header">
                          {{ header }}
                      </th>
                  </tr>
              </thead>
              <transition-group name="row" tag="tbody">
                  <tr v-for="(row, idx) in flattenedGlTable" :key="row.key || idx" 
                      :class="getTableRowClass(row)" 
                      :data-testid="row.isHeader ? 'reconciliation__gl-group-header' : 'reconciliation__gl-row'"
                      @click="row.isHeader && row.hasChildren ? toggleGroupCollapse(row.groupId) : null">
                      <td v-for="(header, headerIdx) in glTableHeaders" :key="`${idx}-${headerIdx}`"
                          :class="getTableCellClass(row, header, headerIdx === 0)" 
                          :data-testid="row.isHeader ? 'reconciliation__gl-group-cell' : 'reconciliation__gl-cell'">
                          <template v-if="headerIdx === 0 && row.isHeader && row.hasChildren">
                              <span class="collapse-icon">
                                  <v-icon size="16" :class="row.isCollapsed ? 'collapsed' : 'expanded'">
                                      {{ row.isCollapsed ? 'mdi-chevron-right' : 'mdi-chevron-down' }}
                                  </v-icon>
                              </span>
                          </template>
                          <template v-if="typeof row[header] === 'number'">
                              {{ $nova.formatNumber(row[header].toFixed(0)) }}
                          </template>
                          <template v-else>
                              {{ row[header] }}
                          </template>
                      </td>
                  </tr>
              </transition-group>
          </template>
      </v-simple-table>

      <!-- FC Table (fallback) -->
      <v-simple-table dense class="reconciliation-table" v-if="showFcTable" data-testid="reconciliation__fc-table">
          <template v-slot:default>
              <thead class="tableHeaderBg">
                  <tr class="table-header-row" data-testid="reconciliation__fc-header-row">
                      <th class="col-particulars" data-testid="reconciliation__fc-header">{{ $constants.RECONCILIATION_TABLE.TEXT.PARTICULARS }}</th>
                      <th class="col-sub-amount" data-testid="reconciliation__fc-header">{{ $constants.RECONCILIATION_TABLE.TEXT.SUB_AMOUNT }}</th>
                      <th class="col-final-amount" data-testid="reconciliation__fc-header">{{ $constants.RECONCILIATION_TABLE.TEXT.FINAL_AMOUNT }}</th>
                      <th class="col-file-reference" data-testid="reconciliation__fc-header">{{ $constants.RECONCILIATION_TABLE.TEXT.FILE_REFERENCE }}</th>
                  </tr>
              </thead>
              <transition-group name="row" tag="tbody">
                  <tr v-for="row in flattenedFcTable" :key="row.key" 
                      :class="getFcRowClass(row)" 
                      :data-testid="row.isHeader ? 'reconciliation__fc-group-header' : 'reconciliation__fc-row'"
                      @click="row.isHeader && row.hasChildren ? toggleGroupCollapse(row.groupId) : null">
                      <td :class="getFcCellClass(row, 'particulars')" class="col-particulars" :data-testid="row.isHeader ? 'reconciliation__fc-group-particulars' : 'reconciliation__fc-cell-particulars'">
                          <span v-if="row.isHeader && row.hasChildren" class="collapse-icon">
                              <v-icon size="16" :class="row.isCollapsed ? 'collapsed' : 'expanded'">
                                  {{ row.isCollapsed ? 'mdi-chevron-right' : 'mdi-chevron-down' }}
                              </v-icon>
                          </span>
                          {{ row.particulars }}
                      </td>
                      <td :class="getFcCellClass(row, 'sub-amount')" class="col-sub-amount" :data-testid="row.isHeader ? 'reconciliation__fc-group-sub-amount' : 'reconciliation__fc-cell-sub-amount'">
                          {{ row.subAmount }}
                      </td>
                      <td :class="getFcCellClass(row, 'final-amount')" class="col-final-amount" :data-testid="row.isHeader ? 'reconciliation__fc-group-final-amount' : 'reconciliation__fc-cell-final-amount'">
                          {{ row.finalAmount }}
                      </td>
                      <td class="col-file-reference" :data-testid="row.isHeader ? 'reconciliation__fc-group-file-ref' : 'reconciliation__fc-cell-file-ref'">
                          {{ row.fileRef }}
                      </td>
                  </tr>
              </transition-group>
          </template>
      </v-simple-table>
  </div>
  <div data-testid="reconciliation" v-else>
    <ReconciliationEmptyState />
  </div>
  
</template>

<script>
import { loadTransactionMatchingAnalysis, loadOverallReconciliationSummary } from "../../../../../utils/taskdetail";
import { isTaskBotLinked, getTbTotals, calculateUnexplainedDifference, groupReconciliationData } from "../../../../../utils/utils";
import { TM_RECONCILIATION } from "../../../../../utils/constants";
import ReconciliationEmptyState from './ReconciliationEmptyState.vue';

export default {
  name: "ReconciliationView",
  components: { ReconciliationEmptyState },
  props: {
    task: {
      type: Object,
      required: true,
    },
    redesign: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: true,
      error: null,
      TransactionMatchingAnalysisData: [],
      summaryCollection: null,
      overallSummary: [],
      annexure: [],
      hasTableData: false,
      selectedTableType: 'entity',
      tableTypes: this.$constants.RECONCILIATION_TABLE.TABLE_TYPES,
      collapsedGroups: {} // Track which groups are collapsed (default expanded)
    };
  },
  created() {
    // Centralize table access via computed tableData to reduce branching
    const table = this.tableData || {};
    this.hasTableData = !!(table.entity_table?.length || table.gl_table?.length || table.fc_table?.length);
    this.glTable = table.gl_table || [];
    this.entityTable = table.entity_table || [];
    this.fcTable = table.fc_table || [];

    // Set default table type based on what's available
    if (this.entityTable.length > 0) {
      this.selectedTableType = 'entity';
    } else if (this.glTable.length > 0) {
      this.selectedTableType = 'gl';
    }

    this.fetchReconciliationData();
  },
  computed: {
    reconciliationConstants() {
      return this.$constants.RECONCILIATION_TABLE;
    },
    /**
     * Grouped overall summary data with group headers
     * Uses the common groupReconciliationData utility function
     */
    groupedOverallSummary() {
      return groupReconciliationData(this.overallSummary, {
        groups: TM_RECONCILIATION.GROUPS,
        groupDisplayOrder: TM_RECONCILIATION.GROUP_DISPLAY_ORDER
      });
    },
    taskData() {
      if (this.task?._id) return this.task;
      const listData = this.$store.getters['tasks/getTaskListData'] || {};
      const checklist = listData?.checklist || [];
      return checklist.find((row) => row && row._id === this.task?._id) || {};
    },
    botFetchKey() {
      const task = this.taskData || {};
      const analysisId = task.bot_analysis_detail?._id || task.tmaid || '';
      return `${task._id || ''}|${task.botid || ''}|${analysisId}`;
    },
    // Single source for nested table structure
    tableData() {
      return this.taskData?.result?.table || {};
    },
    hasEntityAndGlTables() {
      return this.entityTable.length > 0 && this.glTable.length > 0;
    },
    showEntityTable() {
      return this.entityTable.length > 0 && 
        (this.selectedTableType === 'entity' || this.glTable.length === 0);
    },
    showGlTable() {
      return this.glTable.length > 0 && this.selectedTableType === 'gl';
    },
    showFcTable() {
      return !this.entityTable.length && !this.glTable.length && this.fcTable.length > 0;
    },
    entityTableHeaders() {
      if (!this.entityTable || !this.entityTable.length) return [];
      // Filter out isHeader and groupHeader from display
      return Object.keys(this.entityTable[0] || {}).filter(key => key !== 'isHeader' && key !== 'groupHeader');
    },
    glTableHeaders() {
      if (!this.glTable || !this.glTable.length) return [];
      // Filter out isHeader and groupHeader from display
      return Object.keys(this.glTable[0] || {}).filter(key => key !== 'isHeader' && key !== 'groupHeader');
    },
    groupedFcTable() {
      if (!this.fcTable || !this.fcTable.length) return [];
      
      // Group by Type
      const groups = {};
      this.fcTable.forEach(row => {
        const type = row.Type || this.$constants.RECONCILIATION_TABLE.TEXT.RECON_AMOUNT;
        if (!groups[type]) {
          groups[type] = {
            type: type,
            rows: [],
            total: 0
          };
        }
        groups[type].rows.push(row);
        groups[type].total += Number(row.Amount || 0);
      });
      
      return Object.values(groups);
    },
    tbtotals() {
      return getTbTotals(this.taskData);
    },
    unexplainedDiff() {
      return calculateUnexplainedDifference(this.taskData);
    },
    flattenedFcTable() {
      if (!this.groupedFcTable || !this.groupedFcTable.length) return [];
      
      const flattened = [];
      this.groupedFcTable.forEach((group, groupIdx) => {
        const groupId = `group-${groupIdx}`;
        const isCollapsed = this.collapsedGroups[groupId] || false;
        const hasChildren = group.rows && group.rows.length > 0;
        
        // Add group header row
        flattened.push({
          key: `group-header-${groupIdx}`,
          isHeader: true,
          groupId: groupId,
          hasChildren: hasChildren,
          isCollapsed: isCollapsed,
          particulars: group.type,
          fileRef: '',
          subAmount: '',
          finalAmount: this.$nova.formatNumber(Number(group.total || 0).toFixed(0))
        });
        
        // Add individual rows only if group is not collapsed
        if (!isCollapsed && hasChildren) {
          group.rows.forEach((row, rowIdx) => {
            flattened.push({
              key: `group-${groupIdx}-row-${rowIdx}`,
              isHeader: false,
              groupId: groupId,
              particulars: row.Particulars,
              fileRef: row['File Reference (Optional)'] || '',
              subAmount: this.$nova.formatNumber(Number(row.Amount || 0).toFixed(0)),
              finalAmount: ''
            });
          });
        }
      });
      
      if (this.redesign && flattened.length > 0) {
        const reconTotal = this.groupedFcTable.reduce((sum, group) => sum + Number(group.total || 0), 0);
        flattened.push({
          key: 'recon-total',
          isHeader: true,
          isTotalRow: true,
          particulars: 'Total',
          fileRef: '',
          subAmount: '',
          finalAmount: this.$nova.formatNumber(Number(reconTotal).toFixed(0)),
        });
      }

      // Add the two additional summary rows
      flattened.push({
        key: 'unexplained-difference',
        isHeader: true,
        particulars: this.$constants.RECONCILIATION_TABLE.TEXT.UNEXPLAINED_DIFFERENCE,
        fileRef: '',
        subAmount: '',
        finalAmount: this.$nova.formatNumber(Number(this.unexplainedDiff || 0).toFixed(0))
      });
      
      flattened.push({
        key: 'trail-balance-total',
        isHeader: true,
        particulars: this.$constants.RECONCILIATION_TABLE.TEXT.TRAIL_BALANCE_TOTAL,
        fileRef: '',
        subAmount: '',
        finalAmount: this.$nova.formatNumber(Number(this.tbtotals || 0).toFixed(0))
      });
      
      return flattened;
    },
    flattenedEntityTable() {
      if (!this.entityTable || !this.entityTable.length) return [];
      
      // Check if any row has isHeader property
      const hasHeaderProperty = this.entityTable.some(row => Object.prototype.hasOwnProperty.call(row, 'isHeader'));
      if (!hasHeaderProperty) {
        // Return original table structure if no isHeader property exists
        return this.entityTable.map((row, idx) => ({
          ...row,
          key: `entity-row-${idx}`
        }));
      }
      
      const flattened = [];
      
      this.entityTable.forEach((row, idx) => {
        if (row.isHeader === true) {
          if (row.groupHeader === true) {
            // Handle "Explained Difference" group header (collapsible, default expanded)
            const groupId = `entity-group-${idx}`;
            const isCollapsed = this.collapsedGroups[groupId] || false;
            
            flattened.push({
              ...row,
              key: `entity-group-header-${idx}`,
              isHeader: true,
              groupId: groupId,
              hasChildren: true,
              isCollapsed: isCollapsed
            });
          } else {
            // Regular header row (non-collapsible)
            flattened.push({
              ...row,
              key: `entity-header-${idx}`,
              isHeader: true,
              hasChildren: false,
              isCollapsed: false
            });
          }
        } else {
          // Non-header rows - find their parent group
          const parentGroupIndex = this.entityTable.slice(0, idx).reverse().findIndex(r => r.isHeader === true && r.groupHeader === true);
          if (parentGroupIndex !== -1) {
            const groupIdx = idx - parentGroupIndex - 1;
            const groupId = `entity-group-${groupIdx}`;
            const isCollapsed = this.collapsedGroups[groupId] || false;
            
            // Only add if parent group is not collapsed
            if (!isCollapsed) {
              flattened.push({
                ...row,
                key: `entity-item-${idx}`,
                isHeader: false,
                groupId: groupId
              });
            }
          } else {
            // Standalone non-header row
            flattened.push({
              ...row,
              key: `entity-row-${idx}`,
              isHeader: false
            });
          }
        }
      });
      
      return flattened;
    },
    flattenedGlTable() {
      if (!this.glTable || !this.glTable.length) return [];
      
      // Check if any row has isHeader property
      const hasHeaderProperty = this.glTable.some(row => Object.prototype.hasOwnProperty.call(row, 'isHeader'));
      if (!hasHeaderProperty) {
        // Return original table structure if no isHeader property exists
        return this.glTable.map((row, idx) => ({
          ...row,
          key: `gl-row-${idx}`
        }));
      }
      
      const flattened = [];
      
      this.glTable.forEach((row, idx) => {
        if (row.isHeader === true) {
          if (row.groupHeader === true) {
            // Handle "Explained Difference" group header (collapsible, default expanded)
            const groupId = `gl-group-${idx}`;
            const isCollapsed = this.collapsedGroups[groupId] || false;
            
            flattened.push({
              ...row,
              key: `gl-group-header-${idx}`,
              isHeader: true,
              groupId: groupId,
              hasChildren: true,
              isCollapsed: isCollapsed
            });
          } else {
            // Regular header row (non-collapsible)
            flattened.push({
              ...row,
              key: `gl-header-${idx}`,
              isHeader: true,
              hasChildren: false,
              isCollapsed: false
            });
          }
        } else {
          // Non-header rows - find their parent group
          const parentGroupIndex = this.glTable.slice(0, idx).reverse().findIndex(r => r.isHeader === true && r.groupHeader === true);
          if (parentGroupIndex !== -1) {
            const groupIdx = idx - parentGroupIndex - 1;
            const groupId = `gl-group-${groupIdx}`;
            const isCollapsed = this.collapsedGroups[groupId] || false;
            
            // Only add if parent group is not collapsed
            if (!isCollapsed) {
              flattened.push({
                ...row,
                key: `gl-item-${idx}`,
                isHeader: false,
                groupId: groupId
              });
            }
          } else {
            // Standalone non-header row
            flattened.push({
              ...row,
              key: `gl-row-${idx}`,
              isHeader: false
            });
          }
        }
      });
      
      return flattened;
    }
  },
  watch: {
    botFetchKey() {
      this.fetchReconciliationData();
    },
  },
  methods: {
    isTaskBotLinked,
    async fetchReconciliationData() {
      this.loading = true;
      this.error = null;
      try {
        const analysisId = this.taskData?.bot_analysis_detail?._id || this.taskData?.tmaid;
        const reconciliationParams = analysisId ? {
          id: analysisId,
          bid: this.taskData.botid || null,
          pid: this.taskData.project || null
        } : {};
        const { id, pid, bid } = reconciliationParams || {};

        if (!id || !pid || !bid) {
          console.error("Missing required parameters: id, pid, and bid are all required.");
          return;
        }
        const analysisResponse = await loadTransactionMatchingAnalysis({ id, pid, bid }, this.axios);
        if (!analysisResponse || analysisResponse.status !== "success") {
          throw new Error(analysisResponse?.message || "Failed to load transaction matching analysis");
        }

        if (!Array.isArray(analysisResponse.data) || !analysisResponse.data.length) {
          throw new Error("No data returned from transaction matching analysis");
        }

        const runDetails = analysisResponse.data[0]?.rundetails;
        if (!Array.isArray(runDetails) || !runDetails.length) {
          throw new Error("No rundetails data found in transaction matching analysis");
        }

        this.TransactionMatchingAnalysisData = runDetails;

        const annexure = runDetails[0]?.annexure;
        if (!Array.isArray(annexure) || !annexure.length) {
          throw new Error("No annexure data found in transaction matching analysis");
        }

        this.annexure = annexure;

        this.summaryCollection = annexure.find((item) => (item.name === this.$constants.RECONCILIATION_TABLE.TEXT.RECONCILLATION_SUMMARY_API ||
          item.name === this.$constants.RECONCILIATION_TABLE.TEXT.RECONCILIATION_SUMMARY_API
        ))?.collection;

        if (this.summaryCollection) {
          const summaryResponse = await loadOverallReconciliationSummary(this.summaryCollection, this.axios);

          if (!summaryResponse || summaryResponse.status !== "success") {
            console.warn("Overall reconciliation summary load warning:", summaryResponse?.message);
            return;
          }
          this.overallSummary = this.indexOrder(summaryResponse.data);
          this.removeExcluded();
        } else {
          throw new Error("Analysis has not yet been run ");
        }
      } catch (err) {
        console.error("API Error:", err);
        this.error = err.message || "An error occurred while loading reconciliation data";
      } finally {
        this.loading = false;
      }
    },

    indexOrder(items) {
      if (!Array.isArray(items) || items.length === 0) return [];
      return items.every((item) => item?.index !== undefined)
        ? [...items].sort((a, b) => a.index - b.index)
        : items;
    },

    removeExcluded() {
      this.overallSummary = this.overallSummary.filter((item) => {
        if (item.Reconciliation === this.$constants.RECONCILIATION_TABLE.TEXT.TOTAL_EXCLUDED) {
          const parsed = this.parsedAmount(item.Amount);
          return !(Array.isArray(parsed.LESS) && parsed.LESS.length === 0 &&
                   Array.isArray(parsed.ADD) && parsed.ADD.length === 0);
        }
        return true;
      });
    },

    parsedAmount(amount) {
      try {
        if (!amount || amount.trim() === "") return {};
        return JSON.parse(amount.replace(/'/g, '"'));
      } catch (e) {
        console.error("Error parsing Amount:", e);
        console.error("Invalid amount value:", amount);
        return {};
      }
    },
    // Format Sub Amount values without turning empty/blank into 0
    formatSubAmount(val) {
      // Preserve a real numeric 0, but hide null/undefined/blank strings
      if (val === null || val === undefined) return '';
      if (typeof val === 'string' && val.trim() === '') return '';
      const num = Number(val);
      if (Number.isNaN(num)) return '';
      // Use the existing global formatter; no forced integer rounding here
      return this.$nova.formatNumber(num);
    },
    // Toggle collapse/expand state for a group
    toggleGroupCollapse(groupId) {
      this.$set(this.collapsedGroups, groupId, !this.collapsedGroups[groupId]);
    },
    // Helper methods for consistent CSS classes
    getHeaderClass(header) {
      return header === 'Particulars' ? 'text-left' : 'text-right';
    },
    getCellClass(header) {
      return header === 'Particulars' ? '' : 'text-right';
    },
    getFcRowClass(row) {
      if (row.isTotalRow) return 'recon-total-row';
      const baseClass = row.isHeader ? 'group-header' : 'group-item';
      const collapsibleClass = (row.isHeader && row.hasChildren) ? 'collapsible-header' : '';
      return `${baseClass} ${collapsibleClass}`.trim();
    },
    getFcCellClass(row, cellType) {
      const classes = [];
      if (row.isHeader) classes.push('font-weight-bold');
      if (cellType === 'sub-amount' || cellType === 'final-amount') classes.push('text-right');
      return classes.join(' ');
    },
    // Unified helper methods for Entity and GL Tables
    getTableRowClass(row) {
      if (!row.isHeader) return 'group-item';
      const baseClass = 'group-header';
      const collapsibleClass = (row.isHeader && row.hasChildren) ? 'collapsible-header' : '';
      return `${baseClass} ${collapsibleClass}`.trim();
    },
    getTableCellClass(row, header, isFirstColumn) {
      const classes = [];
      if (row.isHeader) classes.push('font-weight-bold');
      if (!isFirstColumn) classes.push('text-right');
      return classes.join(' ');
    },
    /**
     * Get CSS class for a summary row based on its group and header status.
     * @param {Object} item - Row item with _isGroupHeader and Group properties
     * @returns {Object} Class object for Vue :class binding
     */
    getRowClass(item) {
      return {
        'group-header-row': item._isGroupHeader,
        'table-total-row': item.Group === TM_RECONCILIATION.GROUPS.TABLE_1_TOTAL,
        'table-2-total-row': item.Group === TM_RECONCILIATION.GROUPS.TABLE_2_TOTAL,
        'group-item-row': item._isGroupItem
      };
    },
  },
};
</script>

<style scoped>
/* ===== CARD LAYOUT ===== */
.v-card {
  width: 100%;
  max-width: 900px;
}

.table-selector {
  font-size: var(--font-size-xs);
}

.table-selector-width {
  max-width: 200px;
}

/* ===== TABLE BASE STYLES ===== */
.reconciliation-table {
  border-radius: 0 !important;
  overflow: hidden;
}

.reconciliation-table table {
  table-layout: fixed;
  width: 100%;
}

/* ===== HEADER STYLING ===== */
.table-header-row th {
  font-weight: var(--font-weight-bold);
  padding: var(--space-sm) var(--space-md);
  border-bottom: var(--border-md) solid #e0e0e0;
}

/* ===== COLUMN WIDTHS - FC TABLE ===== */
.reconciliation-table .col-particulars {
  width: 45%;
  text-align: left;
}

.reconciliation-table .col-file-reference {
  width: 25%;
  text-align: left;
}

.reconciliation-table .col-sub-amount {
  width: 15%;
  text-align: right;
}

.reconciliation-table .col-final-amount {
  width: 15%;
  text-align: right;
}

/* ===== HEADER ALIGNMENT - FC TABLE ===== */
.reconciliation-table .table-header-row .col-particulars {
  text-align: left;
}

.reconciliation-table .table-header-row .col-file-reference {
  text-align: left;
}

.reconciliation-table .table-header-row .col-sub-amount {
  text-align: right;
}

.reconciliation-table .table-header-row .col-final-amount {
  text-align: right;
}

/* ===== COLUMN WIDTHS - SUMMARY TABLE ===== */
.reconciliation-table .col-description {
  width: 50%;
  text-align: left;
}

/* ===== COMMON ALIGNMENT ===== */
.text-right {
  text-align: right;
}

.text-left {
  text-align: left;
}

/* ===== ROW STYLING ===== */
.group-header {
  font-weight: var(--font-weight-bold);
}
.collapsible-header {
  cursor: pointer;
  user-select: none;
}

/* ===== COLLAPSE ICON ===== */
.collapse-icon {
  display: inline-block;
  margin-right: var(--space-xs);
  vertical-align: middle;
}

.collapse-icon .v-icon {
  transition: transform var(--duration-med) var(--ease-standard);
}

.collapse-icon .v-icon.collapsed {
  transform: rotate(0deg);
}

.collapse-icon .v-icon.expanded {
  transform: rotate(0deg);
}

/* ===== ROW TRANSITIONS ===== */
tbody tr {
  transition: opacity var(--duration-med) var(--ease-standard), transform var(--duration-med) var(--ease-standard);
}

tbody tr.row-enter-active,
tbody tr.row-leave-active {
  transition: all var(--duration-med) var(--ease-standard);
}

tbody tr.row-enter,
tbody tr.row-leave-to {
  opacity: 0;
  transform: translateY(calc(-1 * var(--space-5)));
}

/* ===== CELL PADDING ===== */
.reconciliation-table td,
.reconciliation-table th {
  padding: var(--space-xs) var(--space-sm);
  border-bottom: var(--border-sm) solid #e0e0e0;
}

/* ===== SUMMARY SPECIFIC STYLES ===== */
.summary-indent {
  margin-left: var(--space-xs);
}

/* ===== TABLE 1 TOTAL ROW STYLING ===== */
::v-deep .table-total-row,
.table-total-row {
  background-color: #FFF3E0 !important;
}

::v-deep .table-total-row td,
.table-total-row td {
  background-color: #FFF3E0 !important;
}

::v-deep .table-total-row:hover,
.table-total-row:hover {
  background-color: #FFE0B2 !important;
}

::v-deep .table-total-row:hover td,
.table-total-row:hover td {
  background-color: #FFE0B2 !important;
}

.table-total-badge {
  display: inline-block;
  margin-left: var(--space-xs);
  padding: var(--space-1) var(--space-xs);
  background-color: #E65100;
  color: #FFFFFF;
  font-size: var(--font-size-3xs);
  font-weight: var(--font-weight-semibold);
  border-radius: var(--radius-4);
  vertical-align: middle;
}

/* ===== GROUP HEADER ROW STYLING ===== */
::v-deep .group-header-row,
.group-header-row {
  background-color: #ECEFF1 !important;
}

::v-deep .group-header-row td,
.group-header-row td {
  background-color: #ECEFF1 !important;
}

.group-header-cell {
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-13);
  color: #37474F;
  padding: var(--space-5) var(--space-sm) !important;
}

/* ===== GROUP ITEM INDENT ===== */
.group-item-indent {
  padding-left: var(--space-lg) !important;
}

/* ===== TABLE 2 TOTAL ROW STYLING ===== */
::v-deep .table-2-total-row,
.table-2-total-row {
  background-color: #E3F2FD !important;
}

::v-deep .table-2-total-row td,
.table-2-total-row td {
  background-color: #E3F2FD !important;
}

::v-deep .table-2-total-row:hover,
.table-2-total-row:hover {
  background-color: #BBDEFB !important;
}

::v-deep .table-2-total-row:hover td,
.table-2-total-row:hover td {
  background-color: #BBDEFB !important;
}

/* ===== RESPONSIVE BEHAVIOR ===== */
@media (max-width: 768px) {
  .reconciliation-table .col-particulars {
    width: 40%;
  }
  
  .reconciliation-table .col-file-reference {
    width: 30%;
  }
  
  .reconciliation-table .col-sub-amount,
  .reconciliation-table .col-final-amount {
    width: 15%;
  }
}
</style>
